import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const WhyWeNeedCommunity = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Community</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              Why We Need Community – Tribe as a Source of Strength
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>August 9, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>4 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share Article
              </Button>
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/97b23649-1a4e-41c6-a0e7-bfc8b650b671.png" 
              alt="Circle of people gathered in a thatched-roof sanctuary representing community and tribal connection"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic">
              Humans are social beings. From the earliest tribes to modern societies, our survival and well-being have depended on connection. Today, despite digital connectivity, many people report feeling lonelier than ever.
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The importance of belonging</h2>

              <p>
                Belonging is a fundamental human need. Research shows that <strong className="text-accent">social isolation increases the risk of premature death as much as smoking 15 cigarettes a day</strong> (Holt-Lunstad et al., 2015). Communities provide emotional support, identity, and resilience.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Why tribes matter</h2>

              <p>
                In group settings, individuals experience lower stress, greater motivation, and improved health outcomes. Rituals, shared meals, and collective practices reinforce bonds. <strong className="text-accent">Retreats and group activities often feel transformative precisely because they recreate this sense of tribe</strong>.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Building modern tribes</h2>

              <ul className="space-y-3 ml-6">
                <li><strong className="text-accent">Prioritize time with supportive groups</strong> – friends, colleagues, neighbors.</li>
                <li><strong className="text-accent">Engage in community activities</strong> and volunteering.</li>
                <li><strong className="text-accent">Create shared rituals</strong> that foster belonging.</li>
              </ul>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Conclusion</h2>

              <p>
                True healing rarely happens in isolation. We need each other. By rebuilding community and rediscovering tribe, we find strength, meaning, and joy.
              </p>

              <p className="text-xl font-light italic text-accent">
                In the circle of community, we remember we are not alone.
              </p>

              <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>• Holt-Lunstad, J., Smith, T. B., Baker, M., Harris, T., & Stephenson, D. (2015). Loneliness and social isolation as risk factors for mortality: A meta-analytic review. <em>Perspectives on Psychological Science, 10</em>(2), 227–237.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default WhyWeNeedCommunity;